import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

// project-imports
import { useUserContext } from "../../context/user_context";
import RegisterFormStencil from "./RegisterFormStencil";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import BreadcrumbOne from "../../common/breadcrumb/BreadcrumbOne";
import { isBlockedDomain, toggle_password } from "../../utils";
import { GoogleAnalyticsEnum, pageView, triggerFunction } from "../../utils/google-analytics";
import Spinner from '../spinner-component/SpinnerOne';

// reading env variable start here
const INVALID_USERNAME_ERROR_MESSAGE = process.env.REACT_APP_INVALID_USERNAME_ERROR_MESSAGE;
const INVALID_NAME_ERROR_MESSAGE = process.env.REACT_APP_INVALID_FULLNAME_ERROR_MESSAGE;
const INVALID_EMAIL_ERROR_MESSAGE = process.env.REACT_APP_INVALID_EMAIL_ERROR_MESSAGE;
const INVALID_PASSWORD_ERROR_MESSAGE = process.env.REACT_APP_INVALID_PASSWORD_ERROR_MESSAGE;
const EMAIL_ERROR_MESSAGE = process.env.REACT_APP_EMAIL_ERROR_MESSAGE;
const PASSWORD_ERROR_MESSAGE = process.env.REACT_APP_PASSWORD_ERROR_MESSAGE;
const CONFIRM_PASSWORD_ERROR_MESSAGE = process.env.REACT_APP_CONFIRM_PASSWORD_ERROR_MESSAGE;
const USERNAME_ERROR_MESSAGE = process.env.REACT_APP_USERNAME_ERROR_MESSAGE;
// reading env variable end here

const RegisterForm = ({ formStyle }) => {

  const navigate = useNavigate();

  // Extracting methods and variables from context
  const { handleSignUp, isLoading, checkUsernameAvailability, isCheckUsernameLoading } = useUserContext();

  // Reading data from store
  const { loginData } = useSelector((item) => item.user);

  // State variables
  const [stencilLoading, setStencilLoading] = useState(true);
  const [signUpForm, setSignUpForm] = useState({
    userName: null,
    fullName: null,
    email: null,
    password: null,
    gender: null,
    confirmPassword: null,
  });
  const [inputValidationErrors, setInputValidationErrors] = useState({
    userName: "",
    fullName: "",
    emailError: "",
    passwordError: "",
    confirmPassword: "",
    genderError: "",
  });

  // Constant variable
  const patterns = {
    userName: /^(?!.*\\.\\.)(?!.*[._-]$)[a-zA-Z0-9._]{3,30}$/,
    fullName: /^[A-Z][a-zA-Z]*((\s[A-Z][a-zA-Z]*)*)$/,
    email:
      /^[a-zA-Z0-9!#$%&*+=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&*=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2,}(?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/,
    password:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=^[^@$!%*?&]*[@$!%*?&][^@$!%*?&]*$)[A-Za-z\d@$!%*?&]{8,16}$/,
    confirmPassword:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=^[^@$!%*?&]*[@$!%*?&][^@$!%*?&]*$)[A-Za-z\d@$!%*?&]{8,16}$/,
  };

  setTimeout(() => {
    setStencilLoading(false);
  }, 1000);

  // Constant functions
  const handleChange = (e) => {
    try {
      let name = e.target.name;
      let value = e.target.value;
      let member = signUpForm;
      if (name === "fullName") {
        member.fullName = value;
      } else if (name === "userName") {
        member.userName = value;
      } else if (name === "email") {
        member.email = value;
      } else if (name === "password") {
        member.password = value;
      } else if (name === "confirmPassword") {
        member.confirmPassword = value;
      } else if (name === "gender") {
        member.gender = value;
      }
      setInputValidationErrors((prev) => ({ ...prev, ...validationInput(member, name) }));
      setSignUpForm(member);
    } catch (error) {
      console.log("89 : RegisterForm : Error while changing input :", error && JSON.stringify(error));
      throw error;
    }
  };

  const validationInput = (member, name) => {
    try {
      let validationState = {};
      if (name === "userName" || name === "all") {
        if (member && (!member.userName || (member.userName && !member.userName.length))) {
          validationState.userName = "Username is required";
        } else if (member && member.userName && (member.userName.length < 3 || member.userName.length > 30)) {
          validationState.userName = "Username must be 3-30 characters long which includes number and underscore(_)";
        } else if (member.userName && member.userName.length && patterns["userName"] && !(patterns["userName"].test(member.userName))) {
          validationState.userName = USERNAME_ERROR_MESSAGE;
        } else if (member.userName && member.userName.length && isBlockedDomain(member.userName)) {
          validationState.userName = INVALID_USERNAME_ERROR_MESSAGE;
        } else {
          validationState.userName = "";
        }
      }

      if (name === "fullName" || name === "all") {
        if (member && (!member.fullName || (member.fullName && !member.fullName.length))) {
          validationState.fullName = "Full Name is required";
        } else if (member && member.fullName && (member.fullName.length < 6 || member.fullName.length > 65)) {
          validationState.fullName = "Full Name must be 6-65 characters long";
        } else if (member.fullName && member.fullName.length && patterns["fullName"] && !(patterns["fullName"].test(member.fullName))) {
          validationState.fullName = "Full Name must be in CamelCase and characters only";
        } else if (member.fullName && member.fullName.length && isBlockedDomain(member.fullName)) {
          validationState.fullName = INVALID_NAME_ERROR_MESSAGE;
        } else {
          validationState.fullName = "";
        }
      }

      if (name === "email" || name === "all") {
        if (member && (!member.email || (member.email && !member.email.length))) {
          validationState.emailError = "Email is required";
        } else if (member.email && member.email.length && patterns["email"] && !(patterns["email"].test(member.email))) {
          validationState.emailError = EMAIL_ERROR_MESSAGE;
        } else if (member.email && member.email.length && isBlockedDomain(member.email)) {
          validationState.emailError = INVALID_EMAIL_ERROR_MESSAGE;
        } else {
          validationState.emailError = "";
        }
      }

      if (name === "password" || name === "all") {
        if (member && (!member.password || (member.password && !member.password.length))) {
          validationState.passwordError = "Password is required";
        } else if (member.password && member.password.length && patterns["password"] && !(patterns["password"].test(member.password))) {
          validationState.passwordError = PASSWORD_ERROR_MESSAGE;
        } else if (member.password && member.password.length && isBlockedDomain(member.password)) {
          validationState.passwordError = INVALID_PASSWORD_ERROR_MESSAGE;
        } else {
          validationState.passwordError = "";
        }
      }

      if (name === "confirmPassword" || name === "all") {
        if (member && (!member.confirmPassword || (member.confirmPassword && !member.confirmPassword.length))) {
          validationState.confirmPassword = "Confirm Password is required";
        } else if (member.confirmPassword && member.confirmPassword.length && patterns["confirmPassword"] && !(patterns["confirmPassword"].test(member.confirmPassword))) {
          validationState.confirmPassword = PASSWORD_ERROR_MESSAGE;
        } else if (member.confirmPassword && member.confirmPassword.length && !(member.confirmPassword === member.password)) {
          validationState.confirmPassword = CONFIRM_PASSWORD_ERROR_MESSAGE;
        } else if (member.confirmPassword && member.confirmPassword.length && isBlockedDomain(member.confirmPassword)) {
          validationState.confirmPassword = INVALID_PASSWORD_ERROR_MESSAGE;
        } else {
          validationState.confirmPassword = "";
        }
      }

      if (name === "gender" || name === "all") {
        if (member && (!member.gender || (member.gender && !member.gender.length))) {
          validationState.genderError = "Gender is required";
        } else {
          validationState.genderError = "";
        }
      }
      setInputValidationErrors(validationState);
      return validationState;
    } catch (error) {
      console.log("163 : RegisterForm : Error while adding validation :", error && JSON.stringify(error));
      throw error;
    }
  };

    const handleUsernameAvailability = async () => {
    try {
      if (!signUpForm?.userName || signUpForm?.userName.length < 3 || signUpForm?.userName.length > 30) return;

      const response = await checkUsernameAvailability(signUpForm.userName);

      // Adjust 'response.data.isAvailable' based on your exact API architecture
      const isTaken = response && (response.isAvailable === false || response.data?.isAvailable === false);

      if (isTaken) {
        setInputValidationErrors((prev) => ({
          ...prev,
          userName: "Username is already taken."
        }));
        return false;
      }

      return true;
    } catch (error) {
      console.log("201 : RegidterForm : Error checking username availability:", error);
    }
  };

  const handleSignUpForm = async () => {
    try {
      let formIsValid = true;
      const validationErrros = validationInput(signUpForm, "all");
      if (validationErrros.userName || validationErrros.fullName || validationErrros.email || validationErrros.password || validationErrros.confirmPassword || validationErrros.genderError) {
        formIsValid = false;
      }

      // 2. Run backend uniqueness check before proceeding
      const isUsernameFree = await handleUsernameAvailability();
      if (!isUsernameFree) return; 

      if (formIsValid) {
        const res = await handleSignUp({
          ...signUpForm,
        });
        // delete signUpForm["confirmPassword"];
        triggerFunction(GoogleAnalyticsEnum.BUTTON, GoogleAnalyticsEnum.REGISTER_SUBMIT_BUTTON);
        // if (res && res?.statusCode === 200) {
        //   navigate('/login', { replace: true });
        // }
        if (res) {
          navigate('/login', { replace: true });
        }
      }
    } catch (error) {
      console.log("187 : RegisterForm : Error while submit details => ", error && JSON.stringify(error));
      throw error;
    }
  };

  useEffect(() => {
    if (loginData && loginData?.token && loginData?.token.length) {
      navigate('/', { replace: true });
    }
  }, [loginData]);

  return (
    <>
      {pageView(window.location.pathname, GoogleAnalyticsEnum.LSA_REGISTER_FORM_VIEWED)}
      {isLoading && <Spinner />}
      <SEO title="Longswitch Academy Register form" url="https://longswitchacademy.com/register" />
      <Layout searchDisable={true}>
        <BreadcrumbOne
          title="Register"
          rootUrl="/"
          parentUrl="Home"
          currentUrl="Register"
        />
        <div className="login-register-page-wrapper edu-section-gapBottom bg-color-white">
          <div className="container checkout-page-style">
            <div className="row g-5">
              <div className="col-lg-6 mx-auto">
                {stencilLoading ? <RegisterFormStencil /> :
                  <div className="register-form-box">
                    <h3 className="text-center mb-0 fs-30">Create An Account</h3>
                    <p className="text-center mb--30 fs-15">Sign up to get started learning with longswitch academy.</p>
                    <div className={`${formStyle}`}>
                      <div className="input-box mb--30">
                        <label className="mb-2">
                          Username
                        </label>
                        <input
                          disabled={isLoading}
                          type="text"
                          name="userName"
                          placeholder="e.g Shubham_Prajapati "
                          value={signUpForm?.userName || ""}
                          onChange={handleChange}
                          onBlur={handleUsernameAvailability}
                        // required={true}
                        className="fs-15"
                        />
                        {inputValidationErrors && inputValidationErrors.userName && (
                          <div className="invalid-data">{inputValidationErrors.userName}</div>
                        )}
                      </div>
                      <div className="input-box mb--30">
                        <label className="mb-2">
                          Full Name
                        </label>
                        <input
                          disabled={isCheckUsernameLoading}
                          type="text"
                          name="fullName"
                          placeholder="e.g Shubham Prajapati"
                          value={signUpForm?.fullName || ""}
                          onChange={handleChange}
                        // required={true}
                        className="fs-15"
                        />
                        {inputValidationErrors && inputValidationErrors.fullName && (
                          <div className="invalid-data">{inputValidationErrors.fullName}</div>
                        )}
                      </div>
                      <div className="input-box mb--30">
                        <label className="mb-2">
                          E-Mail
                        </label>
                        <input
                          disabled={isCheckUsernameLoading}
                          type="email"
                          placeholder="e.g shubham.prajapati@gmail.com"
                          value={signUpForm?.email || ""}
                          name="email"
                          pattern="^[a-zA-Z0-9!#$%&*+=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&*=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2,}(?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$"
                          // autoFocus
                          // required={true}
                          title="Please Enter Valid Email"
                          onChange={handleChange}
                          className="fs-15"
                        />
                        {inputValidationErrors && inputValidationErrors.emailError && (
                          <div className="invalid-data">{inputValidationErrors.emailError}</div>
                        )}
                      </div>
                      <div className="input-box mb--30">
                        <label className="mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          id="password"
                          disabled={isCheckUsernameLoading}
                          placeholder="e.g Shubham@123"
                          value={signUpForm?.password || ""}
                          name="password"
                          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=^[^@$!%*?&]*[@$!%*?&][^@$!%*?&]*$)[A-Za-z\d@$!%*?&]{8,16}$"
                          // autoFocus
                          autoComplete="false"
                          // required={true}
                          title="Please Enter The valid Password"
                          onChange={handleChange}
                          className="fs-15"
                        />
                        {inputValidationErrors && inputValidationErrors.passwordError &&
                          <div className="invalid-data">{inputValidationErrors.passwordError}</div>
                        }
                      </div>
                      <div className="input-box mb--30">
                        <label className="mb-2">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          disabled={isCheckUsernameLoading}
                          placeholder="e.g Shubham@123"
                          value={signUpForm?.confirmPassword || ""}
                          name="confirmPassword"
                          id="confirmPassword"
                          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=^[^@$!%*?&]*[@$!%*?&][^@$!%*?&]*$)[A-Za-z\d@$!%*?&]{8,16}$"
                          // autoFocus
                          autoComplete="false"
                          // required={true}
                          title="Please Enter The valid Password"
                          onChange={handleChange}
                          className="fs-15"
                        />
                        {inputValidationErrors && inputValidationErrors.confirmPassword && (
                          <div className="invalid-data">{inputValidationErrors.confirmPassword}</div>
                        )}
                      </div>
                      <div className="input-box mb--30">
                        <label className="mb-2">
                          Gender
                        </label>
                        <select name="gender" defaultValue={'title'} onChange={handleChange} className="fs-15">
                          <option value="title" disabled={true}>
                            Choose Your Gender
                          </option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Female">Others</option>
                        </select>
                        {inputValidationErrors && inputValidationErrors.genderError && (
                          <div className="invalid-data">{inputValidationErrors.genderError}</div>
                        )}
                      </div>
                      <div className="input-box mb--20">
                        <input type="checkbox" id="show-password" onClick={toggle_password} />
                        <label htmlFor="show-password">Show Password</label>
                      </div>
                      {!isLoading && !isCheckUsernameLoading && (
                        <button
                          // disabled={disableSignUpBtn}
                          className={"rn-btn edu-btn w-100"}
                          type="button"
                          onClick={handleSignUpForm}
                        >
                          <span>Sign Up</span>
                          <i className="icon-arrow-right-line-right"></i>
                        </button>
                      )}
                      {isLoading && isCheckUsernameLoading && (
                        <button
                          disabled={isLoading}
                          className={"rn-btn edu-btn w-100 btn-loading"}
                          type="button"
                        >
                          <span
                            className="rn-btn edu-btn spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Setting You Up ...
                        </button>
                      )}
                      <div className="block text-center my-3 fs-15">Already have an account ?<Link to={"/login"} className="fw-bold"> Log in</Link></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};
export default RegisterForm;
